import { ChangeDetectorRef, Component, Inject, OnInit, OnDestroy } from '@angular/core';
import {
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { catchError } from 'rxjs';
import { UserDataService } from '../../services/user-data/user-data.service';
import { OtpErrorMessages } from '../../../shared/constants/otp-errors.constant';

@Component({
    standalone: false,
    selector: 'octo-otp-confirmation-dialog',
    templateUrl: './otp-confirmation-dialog.component.html',
    styleUrls: ['./otp-confirmation-dialog.component.scss'],
})
export class OtpConfirmationDialogComponent implements OnInit, OnDestroy {
    otpForm: UntypedFormGroup;
    submitting = false;
    errText = '';
    limitReached = false;
    otp = '';
    resending = false;
    resendTimeout = true;
    timeLeft = 30;
    resendSecs = '';
    timerFunc: any = null;
    constructor(
        private modalRef: MatDialogRef<OtpConfirmationDialogComponent>,
        private userService: UserDataService,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private fb: UntypedFormBuilder,
        private cdr: ChangeDetectorRef
    ) {
        this.otpForm = this.fb.group({
            otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
        });
    }

    ngOnInit(): void {
        this.timerFunc = setInterval(() => {
            this.countDown();
        }, 1000);
    }

    countDown(): void {
        if (this.timeLeft === 1) {
            clearInterval(this.timerFunc);
            this.resendTimeout = false;
            this.resendSecs = '';
        } else {
            this.timeLeft--;
            this.resendSecs = `Resend in ${this.timeLeft} second${
                this.timeLeft !== 1 ? 's' : ''
            }`;
        }
        this.cdr.detectChanges();
    }

    resendOTP(): void {
        this.resending = true;
        this.cdr.detectChanges();
        this.userService
            .getOTP(this.data?.otpReason)
            .pipe(
                catchError((error) => {
                    this.resending = false;
                    this.errText = this.getOtpErrorMessage(error);
                    if (this.limitReached) {
                        this.clearResendTimer();
                    } else {
                        setTimeout(() => (this.errText = ''), 5000);
                    }
                    this.cdr.detectChanges();
                    throw 'Error';
                })
            )
            .subscribe((res) => {
                if (res.success) {
                    this.resendTimeout = true;
                    this.timeLeft = 30;
                    this.resendSecs = `Resend in ${this.timeLeft} second${
                        this.timeLeft !== 1 ? 's' : ''
                    }`;
                    this.timerFunc = setInterval(() => {
                        this.countDown();
                    }, 1000);
                } else {
                    this.errText = res.message;
                    setTimeout(() => (this.errText = ''), 5000);
                }
                this.resending = false;
                this.cdr.detectChanges();
            });
    }

    /**
     * Build a user friendly message from a failed OTP request. Never exposes
     * the raw HTTP error text. Flags the dialog as locked once the OTP
     * request limit is exhausted.
     */
    getOtpErrorMessage(error: any): string {
        const details = error?.error?.error;
        const otpStatus = details?.otp_status;
        let message =
            OtpErrorMessages[otpStatus] || OtpErrorMessages.exception;
        if (otpStatus === 'exhausted_attempts') {
            this.limitReached = true;
            if (details?.unlock_time_str) {
                message += ` Your account will be unlocked automatically after ${details.unlock_time_str}.`;
            }
        }
        return message;
    }

    private clearResendTimer(): void {
        if (this.timerFunc) {
            clearInterval(this.timerFunc);
            this.timerFunc = null;
        }
        this.resendTimeout = true;
        this.resendSecs = '';
    }

    verify(): void {
        this.submitting = false;
        this.modalRef.close({
            otp: this.otpForm.value?.otp,
            reason: this.data?.otpReason,
        });
    }

    ngOnDestroy(): void {
        if (this.timerFunc) {
            clearInterval(this.timerFunc);
            this.timerFunc = null;
        }
    }
}
